import { useEffect, useMemo, useState } from "react";
import { Check, X, ShieldCheck, AlertTriangle, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RupiahInput from "@/components/RupiahInput";
import DateField from "@/components/DateField";
import { fmtDate, formatIDR } from "@/lib/constants";
import { projectLabel as _pl } from "./utils";
import { pcProofUrl } from "./PettyCashActualDialog";
import AuditLogButton from "@/components/AuditLogButton";

function rowProjectLabel(pc) {
  if (pc.project_type === "variation_order") return `${pc.project_name} · VO ${pc.project_variation || ""}`.trim();
  if (pc.project_type === "working_order") return `${pc.project_name} · WO ${pc.project_wo_number || ""}`.trim();
  return pc.project_name || "Untitled";
}

/**
 * Unified Petty Cash Detail Dialog.
 *
 * TWO ROLES rendered from the same table (per-item row):
 *  - OWNER (pengaju): can edit actual amount/date/cancel per item BEFORE it is filled or
 *    while it is still in a rejected state (allowing re-fill).
 *  - APPROVER (BOD / FAT Lv1-2, not the owner): sees Verify / Reject buttons on the RIGHT
 *    side of each item once the owner has filled it. Rejecting unlocks the item so the owner
 *    can re-fill; verifying locks it. When ALL non-cancelled items are verified, the doc
 *    becomes `is_verified=true` and the pengaju is allowed to submit a new petty cash.
 */
export default function PettyCashDetailDialog({ open, pc, user, isApprover, onClose, onChanged, highlightItemId = null }) {
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [rejectingItemId, setRejectingItemId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  // 2026-10-13 (Tahap 1 verifikasi dua tahap): mode aksi pemeriksa — "reject"
  // (tolak, item dikosongkan) atau "revise" (minta perbaikan, item tetap terisi).
  const [actionMode, setActionMode] = useState("reject");
  // Owner: revisi item yang SUDAH lolos Verifikasi 1 (default terkunci;
  // toggle "Revisi" membuka edit — backend me-reset V1 + audit + tandai COA).
  const [revising, setRevising] = useState({});
  // 149-cw-r160: refs ke setiap item row supaya deep-link `?comment=<item_id>`
  // bisa scroll ke item spesifik + highlight amber ring 3s.
  const itemRefs = useState(() => ({ current: {} }))[0];

  // 149-cw-r160: scroll ke item highlight + amber ring (3s) saat dialog open
  // dan highlightItemId di-set (dari `?comment=<item_id>`).
  useEffect(() => {
    if (!open || !highlightItemId) return;
    const t = setTimeout(() => {
      const el = itemRefs.current[highlightItemId];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t);
  }, [open, highlightItemId, itemRefs]);

  // Seed edit state each time a new pc is opened.
  useMemo(() => {
    if (!pc) { setEdits({}); return; }
    const seed = {};
    (pc.items || []).forEach((it) => {
      seed[it.id] = {
        actual_amount: it.actual_amount != null ? String(it.actual_amount) : "",
        actual_transaction_date: it.actual_transaction_date || (pc.transfer_date || ""),
        voucher_date: it.voucher_date || "",
        note: it.actual_note || "",
        is_cancelled: !!it.is_cancelled,
      };
    });
    setEdits(seed);
    setRejectingItemId(null);
    setRejectNote("");
    setActionMode("reject");
    setRevising({});
  }, [pc?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!pc) return null;

  const isOwner = pc.user_id === user?.id;
  const canEditActual = isOwner && !!pc.transferred_at && !pc.is_verified;
  const totalPengajuan = pc.total || 0;
  const totalTransfer = pc.transfer_amount != null ? pc.transfer_amount : totalPengajuan;

  const setEditField = (itemId, field, value) => {
    setEdits((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [field]: value } }));
  };

  // Compute live totals from edit state (owner) or committed doc data.
  let totalAktual = 0;
  (pc.items || []).forEach((it) => {
    const e = canEditActual ? edits[it.id] : null;
    if (e) {
      if (!e.is_cancelled) totalAktual += Number(String(e.actual_amount ?? "").replace(/[^\d.-]/g, "")) || 0;
    } else if (!it.is_cancelled) {
      totalAktual += Number(it.actual_amount) || 0;
    }
  });
  const sisaKas = totalTransfer - totalAktual;

  const submitEdits = async () => {
    const itemMap = Object.fromEntries((pc.items || []).map((it) => [it.id, it]));
    const items = Object.entries(edits)
      .filter(([item_id]) => {
        const it = itemMap[item_id];
        if (!it) return false;
        // 2026-10-22 (bugfix): item cancelled tidak pernah terkunci — un-cancel
        // harus ikut terkirim ke backend.
        const locked = it.item_verified === true
          ? !revising[item_id]
          : (it.is_cancelled ? false : (!!it.item_actual_filled_at && !it.item_rejected && !it.item_revision_requested));
        return !locked;
      })
      .map(([item_id, v]) => ({
        item_id,
        actual_amount: v.is_cancelled ? 0 : (Number(String(v.actual_amount ?? "").replace(/[^\d.-]/g, "")) || 0),
        actual_transaction_date: v.is_cancelled ? null : (v.actual_transaction_date || null),
        voucher_date: v.is_cancelled ? null : (v.voucher_date || null),
        note: v.note || null,
        is_cancelled: !!v.is_cancelled,
      }));
    if (items.length === 0) { toast.error("Tidak ada item yang bisa diedit — semua sudah terkunci"); return; }
    if (items.some((x) => !x.is_cancelled && !x.voucher_date)) { toast.error("Tanggal voucher wajib diisi untuk setiap item realisasi"); return; }
    setBusy(true);
    try {
      await api.patch(`/petty-cash/${pc.id}/actual`, { items });
      toast.success("Realisasi tersimpan");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // 2026-10-13 (Tahap 2): penetapan COA dipindah ke Verifikasi 2 — halaman
  // antrean "Penetapan COA" (/petty-cash/penetapan-coa). Dialog ini hanya V1.

  // Approver action: Verifikasi 1 per item — verify (sahkan pengeluaran),
  // request_revision (minta perbaikan), reject (tolak administrasi).
  // COA TIDAK wajib di tahap ini — penetapan COA = Verifikasi 2.
  const doVerifyItem = async (itemId, action, note) => {
    if ((action === "reject" || action === "request_revision") && !(note || "").trim()) {
      toast.error(action === "reject" ? "Alasan reject wajib diisi" : "Catatan perbaikan wajib diisi");
      return;
    }
    try {
      const { data } = await api.post(`/petty-cash/${pc.id}/verify-item`, { item_id: itemId, action, note: note || null });
      toast.success(action === "verify" ? "Item lolos Verifikasi 1 (pengeluaran)" : action === "request_revision" ? "Permintaan perbaikan dikirim ke pengaju" : "Item ditolak — pengaju akan diminta input ulang");
      setRejectingItemId(null); setRejectNote(""); setActionMode("reject");
      onChanged?.(data);
      if (data?.is_verified) {
        toast.success("Semua item lolos Verifikasi 1 — pengajuan selesai");
        onClose();
      }
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const showApproverColumn = !!isApprover && !isOwner;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none w-[92vw] max-w-[92vw] max-h-[92vh] overflow-y-auto" data-testid="dlg-pc-detail">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pr-8">
            <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
              Detail Petty Cash
              {showApproverColumn && <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 px-2 py-0.5">Mode Pemeriksa Verifikasi 1</span>}
            </DialogTitle>
            <AuditLogButton feature_key="petty_cash" target_id={pc?.id} title={`${rowProjectLabel(pc)} · ${pc?.week_label || ""}`} size="sm" />
          </div>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><div className="overline text-slate-500 text-[10px]">Project</div><div className="font-medium">{rowProjectLabel(pc)}</div></div>
            <div><div className="overline text-slate-500 text-[10px]">Week</div><div className="font-medium">{pc.week_label}</div></div>
            <div><div className="overline text-slate-500 text-[10px]">Submitter</div><div className="font-medium">{pc.user_name}</div></div>
            <div><div className="overline text-slate-500 text-[10px]">Total Pengajuan</div><div className="font-semibold text-[#FF5A1F]">{formatIDR(totalPengajuan)}</div></div>
          </div>
          {pc.transferred_at && (
            <div className="border border-emerald-200 bg-emerald-50 p-2 space-y-2 text-[11px]">
              <div className="grid grid-cols-3 gap-3">
                <div><div className="overline text-emerald-700 text-[9px]">Ditransfer</div><div className="font-semibold tabular-nums text-emerald-800">{formatIDR(totalTransfer)}</div>{pc.transfer_date && <div className="text-[9px] text-slate-600">{fmtDate(pc.transfer_date)}</div>}</div>
                <div><div className="overline text-amber-700 text-[9px]">Total Aktual (Live)</div><div className="font-semibold tabular-nums text-amber-700">{formatIDR(totalAktual)}</div></div>
                <div><div className="overline text-slate-500 text-[9px]">Sisa Kas</div><div className={`font-semibold tabular-nums ${sisaKas >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatIDR(sisaKas)}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-3 border-t border-emerald-200 pt-2">
                <div data-testid="pc-detail-transfer-source">
                  <div className="overline text-slate-500 text-[9px]">Sumber Dana</div>
                  <div className="font-semibold text-slate-800">
                    {pc.transfer_source?.type === "cash" ? "Cash" : pc.transfer_source?.type === "staff_bank" ? `Staff · ${pc.transfer_source.owner_name || "—"}` : pc.transfer_source?.type === "company_bank" ? "Rekening Perusahaan" : "—"}
                  </div>
                  {pc.transfer_source?.bank_name && <div className="text-[9px] text-slate-600 font-mono">{pc.transfer_source.bank_name} — {pc.transfer_source.account_no}</div>}
                </div>
                <div data-testid="pc-detail-transfer-destination">
                  <div className="overline text-slate-500 text-[9px]">Rekening Tujuan</div>
                  <div className="font-semibold text-slate-800">{pc.transfer_bank_account?.bank_name || "—"}</div>
                  {pc.transfer_bank_account?.account_no && <div className="text-[9px] text-slate-600 font-mono">{pc.transfer_bank_account.account_no}</div>}
                </div>
              </div>
            </div>
          )}
          {pc.note && (
            <div className="text-xs text-slate-700 border-l-2 border-slate-300 pl-2 italic">{pc.note}</div>
          )}
          {canEditActual && (
            <div className="text-[10px] text-amber-800 bg-amber-50 border-l-2 border-amber-400 p-2">
              Anda bisa mengubah nominal &amp; tanggal realisasi per item. Nominal aktual <span className="font-semibold">boleh melebihi pengajuan</span> (misal harga naik) — sistem tidak membatasi. Centang "Tidak Jadi" untuk item yang batal. Perubahan disimpan sebelum verifikasi FAT.
            </div>
          )}
          {showApproverColumn && (
            <div className="text-[10px] text-emerald-800 bg-emerald-50 border-l-2 border-emerald-500 p-2">
              <b>Verifikasi 1 — Pengeluaran:</b> periksa bukti, nominal, tanggal, proyek/tujuan, uraian/vendor, dan sumber dana tiap item yang <b>sudah diisi pengaju</b>. Tindakan: <b>Verify</b> (sahkan), <b>Minta Perbaikan</b> (item tetap terisi, pengaju merevisi), atau <b>Reject</b> (tolak — pengaju input ulang). Penetapan akun COA adalah <b>Verifikasi 2</b> dan <b>tidak wajib</b> di tahap ini.
            </div>
          )}
          <div className="overflow-x-auto border border-slate-200" data-testid="pc-detail-table-wrap">
            <table className="w-full text-xs" style={{ tableLayout: "fixed" }} data-testid="pc-detail-table">
              {/* 16-PETTY-CASH-DETAIL-COLUMN-LAYOUT-R1 (2026-08-26):
                  Proporsi kolom baru — Item/Jasa 300-320px min (paling lebar,
                  wrap 2-3 baris + tooltip); Nomor/Qty/Tidak Jadi diciutkan;
                  Harga/Subtotal/Nominal Aktual medium rata-kanan; Tanggal/
                  Catatan/Verifikasi ruang cukup. `table-layout: fixed` supaya
                  kolom mematuhi widths saat text wrapping. `min-w-[1180px]`
                  memastikan tabel tetap terbaca; horizontal-scroll aktif via
                  wrapper `overflow-x-auto` bila viewport < 1180px. */}
              <colgroup>
                <col style={{ width: "40px" }} />
                <col style={{ minWidth: "300px" }} />
                <col style={{ width: "56px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "68px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "160px" }} />
                {showApproverColumn && <col style={{ width: "180px" }} />}
              </colgroup>
              <thead className="bg-slate-50 uppercase tracking-wider text-[10px] text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5">#</th>
                  <th className="text-left px-2 py-1.5">Item / Jasa</th>
                  <th className="text-right px-2 py-1.5">Qty</th>
                  <th className="text-right px-2 py-1.5">Harga</th>
                  <th className="text-right px-2 py-1.5 bg-slate-100">Subtotal</th>
                  <th className="text-center px-2 py-1.5 bg-rose-50">Tdk Jadi</th>
                  <th className="text-left px-2 py-1.5 bg-amber-50">Tgl Aktual</th>
                  <th className="text-left px-2 py-1.5 bg-amber-50">Tgl Voucher *</th>
                  <th className="text-right px-2 py-1.5 bg-amber-50">Nominal Aktual</th>
                  <th className="text-left px-2 py-1.5">Catatan Realisasi</th>
                  {showApproverColumn && <th className="text-center px-2 py-1.5 bg-emerald-50">Verifikasi 1</th>}
                </tr>
              </thead>
              <tbody>
                {pc.items.map((it, i) => {
                  // 2026-10-13 (verifikasi dua tahap): item verified terkunci default
                  // tapi owner bisa buka via toggle "Revisi" (V1 di-reset + audit).
                  // Item dgn permintaan perbaikan (item_revision_requested) bisa diedit.
                  // 2026-10-22 (bugfix pembatalan penghapusan): item yg di-cancel
                  // ("Tdk Jadi") TIDAK PERNAH terkunci untuk owner — owner harus
                  // bisa menghapus centangnya kembali (batal hapus). Tanpa ini
                  // item masuk stuck state permanen di semua akun pemeriksa.
                  const itemLocked = it.item_verified === true
                    ? !revising[it.id]
                    : (it.is_cancelled ? false : (!!it.item_actual_filled_at && !it.item_rejected && !it.item_revision_requested));
                  const itemEditable = canEditActual && !itemLocked;
                  const e = itemEditable ? (edits[it.id] || {}) : null;
                  const cancelled = itemEditable ? !!e.is_cancelled : !!it.is_cancelled;
                  const actAmt = itemEditable
                    ? (cancelled ? 0 : Number(String(e.actual_amount ?? "").replace(/[^\d.-]/g, "")) || 0)
                    : (it.is_cancelled ? 0 : Number(it.actual_amount) || 0);
                  const actDate = itemEditable ? (e.actual_transaction_date || "") : (it.actual_transaction_date || "");
                  const actVoucherDate = itemEditable ? (e.voucher_date || "") : (it.voucher_date || "");
                  const actNote = itemEditable ? (e.note || "") : (it.actual_note || "");
                  const isFilled = !!it.item_actual_filled_at || (it.actual_amount !== null && it.actual_amount !== undefined);
                  const isItemVerified = it.item_verified === true;
                  const isItemRejected = it.item_rejected === true;
                  const isRejecting = rejectingItemId === it.id;
                  return (
                    <tr
                      key={it.id}
                      ref={(el) => { if (el) itemRefs.current[it.id] = el; else delete itemRefs.current[it.id]; }}
                      className={`border-t border-slate-100 ${cancelled ? "opacity-60 line-through" : ""} ${isItemVerified ? "bg-emerald-50/40" : ""} ${isItemRejected ? "bg-rose-50/40" : ""} ${highlightItemId === it.id ? "!bg-amber-50 ring-2 ring-amber-400 transition-all" : ""}`}
                      data-testid={`pc-detail-row-${it.id}`}
                    >
                      <td className="px-2 py-1 text-slate-500">
                        {i + 1}
                        {isItemVerified && <div className="text-[9px] text-emerald-700 font-bold uppercase">✓ Lolos Verifikasi 1</div>}
                        {isItemRejected && <div className="text-[9px] text-rose-700 font-bold uppercase" title={it.item_reject_note}>⚠ Reject</div>}
                        {it.item_revision_requested && (
                          <div className="text-[9px] text-amber-700 font-bold uppercase" title={it.item_revision_note}>✎ Revisi Diminta</div>
                        )}
                        {isItemVerified && it.coa_status === "awaiting_coa" && (
                          <div className="text-[9px] text-amber-700 font-bold uppercase" data-testid={`pc-detail-coa-awaiting-${it.id}`}>⧗ Menunggu COA</div>
                        )}
                        {it.coa_review_needed && (
                          <div className="text-[9px] text-rose-700 font-bold uppercase" data-testid={`pc-detail-coa-review-${it.id}`}>⚠ COA Tinjau Ulang</div>
                        )}
                        {itemLocked && !isItemVerified && !isItemRejected && !it.item_revision_requested && <div className="text-[9px] text-sky-700 font-bold uppercase">🔒 Terkunci</div>}
                        {isItemVerified && canEditActual && (
                          <button
                            type="button"
                            data-testid={`pc-detail-revise-${it.id}`}
                            onClick={() => setRevising((prev) => ({ ...prev, [it.id]: !prev[it.id] }))}
                            className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700 underline hover:text-amber-900"
                            title="Revisi nominal/tanggal item yang sudah terverifikasi — Verifikasi 1 akan dievaluasi ulang dan perubahan tercatat di audit"
                          >
                            {revising[it.id] ? "Batal Revisi" : "Revisi"}
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top" title={it.name}>
                        <div
                          className="font-medium text-[#0B1D3A]"
                          style={{
                            whiteSpace: "normal",
                            wordBreak: "normal",
                            overflowWrap: "break-word",
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                          data-testid={`pc-detail-name-${it.id}`}
                        >
                          {it.name}
                        </div>
                        {it.note && (
                          <div
                            className="text-[10px] text-slate-500 mt-0.5"
                            style={{ whiteSpace: "normal", wordBreak: "normal", overflowWrap: "break-word", lineHeight: 1.35 }}
                            title={it.note}
                          >
                            {it.note}
                          </div>
                        )}
                        {isItemRejected && it.item_reject_note && (
                          <div className="text-[10px] text-rose-700 border-l-2 border-rose-400 pl-1 mt-1 italic" style={{ whiteSpace: "normal", overflowWrap: "break-word" }}>
                            Alasan: {it.item_reject_note}
                          </div>
                        )}
                        {/* 2026-10-16 (user request): tautan bukti transaksi (foto/PDF)
                            — Verifikasi 1 mengecek nominal input vs bukti ini. */}
                        {it.proof_file && (
                          <a
                            href={pcProofUrl(pc.id, it.id)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`pc-detail-proof-${it.id}`}
                            className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-sky-700 hover:text-sky-900 underline"
                            title={`${it.proof_file.name || "Bukti"} · diupload ${it.proof_file.uploaded_by || "pengaju"}`}
                          >
                            <Paperclip size={10} /> Bukti: {it.proof_file.name || "Lampiran"}
                          </a>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">{it.qty}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatIDR(it.unit_price)}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold bg-slate-50">{formatIDR(it.subtotal)}</td>
                      <td className="px-2 py-1 text-center bg-rose-50/40">
                        {itemEditable ? (
                          <input
                            type="checkbox"
                            data-testid={`pc-detail-cancel-${it.id}`}
                            checked={cancelled}
                            onChange={(ev) => setEditField(it.id, "is_cancelled", ev.target.checked)}
                            className="h-4 w-4 accent-rose-600 cursor-pointer"
                            title="Centang jika item ini tidak jadi dilakukan"
                          />
                        ) : (
                          cancelled ? <span className="text-[9px] font-semibold text-rose-700 uppercase tracking-wider">Batal</span> : <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1 bg-amber-50/40 min-w-[140px]">
                        {itemEditable && !cancelled ? (
                          <DateField
                            testid={`pc-detail-date-${it.id}`}
                            value={actDate}
                            onChange={(v) => setEditField(it.id, "actual_transaction_date", v)}
                          />
                        ) : (
                          <span className="tabular-nums text-slate-700">{actDate ? fmtDate(actDate) : <span className="text-slate-300">—</span>}</span>
                        )}
                      </td>
                      <td className="px-2 py-1 bg-amber-50/40 min-w-[140px]">
                        {itemEditable && !cancelled ? (
                          <DateField
                            testid={`pc-detail-voucher-date-${it.id}`}
                            value={actVoucherDate}
                            onChange={(v) => setEditField(it.id, "voucher_date", v)}
                          />
                        ) : (
                          <span className="tabular-nums text-slate-700">{actVoucherDate ? fmtDate(actVoucherDate) : <span className="text-slate-300">—</span>}</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right bg-amber-50/40 min-w-[140px]">
                        {itemEditable && !cancelled ? (
                          <>
                            <RupiahInput
                              testid={`pc-detail-amount-${it.id}`}
                              value={e.actual_amount ?? ""}
                              onChange={(v) => setEditField(it.id, "actual_amount", v)}
                              placeholder={String(it.subtotal || 0)}
                              className="h-9"
                            />
                            {(() => {
                              const dd = actAmt - (it.subtotal || 0);
                              if (dd === 0 || !actAmt) return null;
                              return (
                                <div className={`text-[9px] tabular-nums mt-0.5 ${dd > 0 ? "text-amber-700" : "text-emerald-700"}`} title={dd > 0 ? "Melebihi pengajuan — diperbolehkan" : "Hemat"}>
                                  {dd > 0 ? "▲ Lebih " : "▼ Hemat "}{formatIDR(Math.abs(dd))}
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <span className="tabular-nums font-semibold text-amber-700">{cancelled ? "—" : formatIDR(actAmt)}</span>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        {itemEditable ? (
                          <Input
                            data-testid={`pc-detail-note-${it.id}`}
                            value={actNote}
                            onChange={(ev) => setEditField(it.id, "note", ev.target.value)}
                            className="rounded-none h-8 text-xs"
                            placeholder={cancelled ? "alasan batal…" : "catatan…"}
                          />
                        ) : (
                          <span className="text-[10px] text-slate-600 italic">{actNote || <span className="text-slate-300">—</span>}</span>
                        )}
                      </td>
                      {showApproverColumn && (
                        <td className="px-2 py-1 bg-emerald-50/40" data-testid={`pc-detail-verify-cell-${it.id}`}>
                          {cancelled ? (
                            <span className="text-[10px] text-slate-500 italic">Dibatalkan — tidak perlu Verifikasi 1</span>
                          ) : isItemVerified ? (
                            <div className="space-y-0.5">
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-700">
                                <ShieldCheck size={11} /> Lolos Verifikasi 1
                              </span>
                              {it.v1_decision?.by_name && (
                                <div className="text-[9px] text-slate-500 normal-case">oleh {it.v1_decision.by_name} · v{it.v1_decision.realization_version || 1}</div>
                              )}
                              {it.coa_code && (
                                <div className="text-[9px] text-slate-500 normal-case" data-testid={`pc-detail-coa-label-${it.id}`}>{it.coa_code} — {it.coa_name}</div>
                              )}
                              {it.coa_review_needed && (
                                <div className="text-[9px] font-semibold uppercase text-rose-700" data-testid={`pc-detail-coa-review-flag-${it.id}`}>COA perlu ditinjau ulang</div>
                              )}
                              {!it.coa_id && (
                                <div className="text-[9px] font-semibold uppercase text-amber-700">Menunggu Verifikasi 2 (COA)</div>
                              )}
                            </div>
                          ) : !isFilled ? (
                            <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 italic">
                              <AlertTriangle size={10} /> Tunggu pengaju
                            </span>
                          ) : isRejecting ? (
                            <div className="space-y-1">
                              <div className={`text-[9px] font-bold uppercase ${actionMode === "reject" ? "text-rose-700" : "text-amber-700"}`}>
                                {actionMode === "reject" ? "Tolak item (pengaju input ulang)" : "Minta perbaikan (item tetap terisi)"}
                              </div>
                              <Input
                                data-testid={`input-reject-note-${it.id}`}
                                placeholder={actionMode === "reject" ? "Alasan reject *" : "Catatan perbaikan *"}
                                value={rejectNote}
                                onChange={(ev) => setRejectNote(ev.target.value)}
                                className="rounded-none h-7 text-xs"
                                autoFocus
                              />
                              <div className="flex gap-1">
                                <button
                                  data-testid={`btn-confirm-reject-${it.id}`}
                                  onClick={() => doVerifyItem(it.id, actionMode === "reject" ? "reject" : "request_revision", rejectNote)}
                                  className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white flex-1 ${actionMode === "reject" ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-600 hover:bg-amber-700"}`}
                                >
                                  {actionMode === "reject" ? "Kirim Reject" : "Kirim Permintaan"}
                                </button>
                                <button
                                  onClick={() => { setRejectingItemId(null); setRejectNote(""); setActionMode("reject"); }}
                                  className="px-2 py-0.5 text-[10px] uppercase text-slate-600 border border-slate-300 hover:bg-slate-100"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <div className="text-[9px] text-slate-500 italic" data-testid={`pc-detail-coa-wajib-${it.id}`}>
                                Penetapan COA dilakukan pada <b>Verifikasi 2</b> setelah item disahkan — via antrean Penetapan COA.
                              </div>
                              <div className="flex gap-1">
                                <button
                                  data-testid={`btn-verify-item-${it.id}`}
                                  onClick={() => doVerifyItem(it.id, "verify")}
                                  title="Verifikasi 1 — sahkan pengeluaran (bukti, nominal, tanggal, proyek, uraian, sumber dana)"
                                  className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 inline-flex items-center gap-1 flex-1"
                                >
                                  <Check size={11} /> Verifikasi 1
                                </button>
                                <button
                                  data-testid={`btn-revise-item-${it.id}`}
                                  onClick={() => { setRejectingItemId(it.id); setRejectNote(""); setActionMode("revise"); }}
                                  title="Minta perbaikan — item tetap terisi, pengaju merevisi (delta tercatat)"
                                  className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-amber-600 hover:bg-amber-700 inline-flex items-center gap-1"
                                >
                                  ✎ Revisi
                                </button>
                                <button
                                  data-testid={`btn-reject-item-${it.id}`}
                                  onClick={() => { setRejectingItemId(it.id); setRejectNote(""); setActionMode("reject"); }}
                                  title="Tolak administrasi — realisasi dikosongkan, pengaju input ulang"
                                  className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 inline-flex items-center gap-1"
                                >
                                  <X size={11} /> Reject
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50">
                  <td colSpan={4} className="px-2 py-1.5 text-right uppercase text-[10px] font-semibold tracking-wider">Total</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold text-[#0B1D3A]">{formatIDR(totalPengajuan)}</td>
                  <td></td><td></td><td></td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-bold text-amber-700">{formatIDR(totalAktual)}</td>
                  <td></td>
                  {showApproverColumn && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none">Tutup</Button>
          {canEditActual && (
            <Button
              data-testid="btn-detail-save-actual"
              onClick={submitEdits}
              disabled={busy}
              className="rounded-none bg-amber-600 hover:bg-amber-700 text-white"
            >
              {busy ? "Menyimpan…" : "Simpan Realisasi"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
